/**
 * Redux Slice Helper Utilities
 * Reusable utilities for Redux async thunks and reducers
 */

/**
 * Standard error handler for async thunks
 * Handles common error patterns: cancellation, API errors, network errors
 * 
 * @param error - Error object from try/catch
 * @param rejectWithValue - Redux Toolkit rejectWithValue function
 * @param defaultMessage - Default error message if none provided
 * @returns Rejected value for the thunk
 * 
 * @example
 * ```typescript
 * export const fetchData = createAsyncThunk(
 *   'slice/fetchData',
 *   async (id: number, { rejectWithValue }) => {
 *     try {
 *       const response = await api.get(`/data/${id}`);
 *       return response.data;
 *     } catch (error: any) {
 *       return handleAsyncThunkError(error, rejectWithValue, 'Failed to fetch data');
 *     }
 *   }
 * );
 * ```
 */
export const handleAsyncThunkError = (
  error: any,
  rejectWithValue: (value: string) => any,
  defaultMessage: string,
): any => {
  // Check for request cancellation
  if (
    error?.message === "canceled" ||
    error?.message === "Request cancelled"
  ) {
    return rejectWithValue("Request cancelled");
  }

  // Extract error message from various sources
  const message =
    error?.response?.data?.message ??
    error?.message ??
    defaultMessage;

  return rejectWithValue(message);
};

/**
 * Extract HTTP status code from error object
 * 
 * @param error - Error object
 * @returns HTTP status code or null if not found
 */
export const getErrorStatus = (error: any): number | null => {
  return error?.response?.status ?? null;
};

/**
 * Handle HTTP status-specific error messages
 * 
 * @param error - Error object
 * @param statusMessages - Map of status codes to error messages
 * @param defaultMessage - Default message if status not in map
 * @returns Error message string
 * 
 * @example
 * ```typescript
 * const message = getStatusBasedErrorMessage(error, {
 *   401: 'Invalid or missing authentication',
 *   403: 'Insufficient permissions',
 *   404: 'Resource not found',
 *   500: 'Internal server error'
 * }, 'Failed to complete request');
 * ```
 */
export const getStatusBasedErrorMessage = (
  error: any,
  statusMessages: Record<number, string>,
  defaultMessage: string,
): string => {
  const status = getErrorStatus(error);
  
  if (status && statusMessages[status]) {
    return statusMessages[status];
  }

  return error?.response?.data?.message ?? error?.message ?? defaultMessage;
};

/**
 * Type guard to check if action payload is a string
 * Useful in extraReducers to safely handle rejectWithValue
 * 
 * @param payload - Action payload
 * @returns True if payload is a string
 */
export const isStringPayload = (payload: unknown): payload is string => {
  return typeof payload === "string";
};

/**
 * Safely extract string error from action payload
 * Returns null if payload is not a string
 * 
 * @param payload - Action payload from rejected thunk
 * @returns Error message or null
 */
export const extractErrorMessage = (payload: unknown): string | null => {
  return isStringPayload(payload) ? payload : null;
};

/**
 * Deep clone an array of objects
 * Creates new object instances to avoid reference issues
 * 
 * @param array - Array to clone
 * @returns Deep cloned array
 * 
 * @example
 * ```typescript
 * state.actionItems = deepCloneArray(state.initialActionItems);
 * ```
 */
export const deepCloneArray = <T>(array: T[]): T[] => {
  return array.map(item => ({ ...item }));
};

/**
 * Create async thunk config with abort signal
 * Standardizes options for API calls with cancellation support
 * 
 * @param signal - AbortSignal from thunk API
 * @returns Config object for axios
 */
export const createThunkConfig = (signal?: AbortSignal): { signal?: AbortSignal } => {
  return signal ? { signal } : {};
};

/**
 * Check if a value is defined (not null or undefined)
 * Type guard for safe value access
 * 
 * @param value - Value to check
 * @returns True if value is defined
 */
export const isDefined = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};

/**
 * Safely access array first element
 * Returns undefined if array is empty or element doesn't exist
 * 
 * @param array - Array to access
 * @returns First element or undefined
 * 
 * @example
 * ```typescript
 * const first = safeGetFirstElement(state.performanceData);
 * if (first) {
 *   first.teamRating = 5;
 * }
 * ```
 */
export const safeGetFirstElement = <T>(array: T[] | null | undefined): T | undefined => {
  return array?.[0];
};
